import { useMemo, useState, useCallback, useRef } from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { useIntl, FormattedMessage } from 'react-intl';
import { useAsync, useAsyncCallback } from 'react-async-hook';
import { ChartProps, Pie } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Field, Form, Formik } from 'formik';

import { Grid, Stack, Typography, Box, CircularProgress } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Column } from 'react-table';
import { RiLineChartLine, RiAlertLine, RiArrowGoBackLine } from 'react-icons/ri';
import Card from 'components/card';
import { Table } from 'components/Table';
import { FinancedPortfolioTable } from 'components/FinancedPortfolioTable';
import { PageContainer } from 'components/UI';
import { Select } from 'components/forms';
import { useFormatMoney } from 'hooks';
import { useCurrencySelection } from 'providers/currency-selection';
import { useLayoutPageTitle } from 'providers/layout-provider';
import { SubmitFormOnChange } from 'components';
import { formatMoney, moveDecimalPoint, toPercent } from 'utils';
import { investorMarketOverviewDataSource, MarketOverviewDataSource } from './marketOverviewDataSource';

const fromCents = (value?: number) => moveDecimalPoint(value ?? 0, -2);

interface ListedOriginatorRow {
  providerId: string;
  originator: string;
  currency: string;
  averageExpectedXirr: number;
  primaryCreditType: string;
  currentPortfolio: number;
  performing: number;
  par: number;
}

interface ListedOriginatorGroupedRow {
  providerId: string;
  originator: string;
  items: ListedOriginatorRow[];
}

interface Values {
  country?: string;
  category?: 'CORPORATE' | 'PRIVATE' | 'REAL_ESTATE' | 'TBC';
  originator?: string;
}

const colors = {
  backgroundColor: ['#254E8F', '#4E84C1', 'rgba(145, 154, 255, 0.95)', '#37A1E4', '#97D7FF', '#97FFF3'],
};

type PieDataset = {
  id?: 'country' | 'creditType' | 'originator';
  data: number[];
  count: string[];
  currency: string | undefined;
  name: string;
  backgroundColor: string[];
};

type PieData = {
  labels: string[];
  datasets: [PieDataset];
};

const useStyles = makeStyles(() => ({
  listedOriginatorsTable: {
    '& .MuiTableCell-root': {
      fontSize: '14px',
    },
    '& .MuiTypography-root': {
      fontSize: '14px',
    },
    '& .MuiTableCell-head': {
      fontWeight: 700,
    },
  },
  listedOriginatorsHeaderText: {
    fontSize: '14px',
    fontWeight: 700,
  },
  portfolioFlowConnection: {
    alignSelf: 'center',
    position: 'relative',
    width: 2,
    height: 32,
    backgroundColor: '#808080',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -5,
      left: -4,
      width: 10,
      height: 10,
      borderRadius: '50%',
      backgroundColor: '#808080',
      zIndex: 1,
    },
  },
}));

const createEmptyPieData = (): PieData => ({
  labels: [],
  datasets: [
    {
      ...colors,
      data: [],
      count: [],
      currency: undefined,
      name: '',
    },
  ],
});


interface Props {
  dataSource?: MarketOverviewDataSource;
}

export const MarketOverviewPage = ({ dataSource = investorMarketOverviewDataSource }: Props) => {
  const classes = useStyles();
  const { selectedCurrency } = useCurrencySelection();
  const intl = useIntl();
  const history = useHistory();
  const { url } = useRouteMatch();
  useLayoutPageTitle(`${intl.formatMessage({ id: 'market_overview' })} (${selectedCurrency})`);

  const formatMoneyHook = useFormatMoney();
  const formatMoneyFromCentsWithCurrency = useCallback(
    (value?: number, currency?: string) => formatMoneyHook(fromCents(value), { prefix: `${currency} ` }),
    [formatMoneyHook],
  );
  const formatMoneyFromCents = useCallback(
    (value?: number) => formatMoneyHook(fromCents(value)),
    [formatMoneyHook],
  );

  const [charts, setCharts] = useState<[PieData, PieData, PieData]>(() => [
    createEmptyPieData(),
    createEmptyPieData(),
    createEmptyPieData(),
  ]);

  const [marketAllocationFilterOptions, setMarketAllocationFilterOptions] = useState<{
    countries: string[];
    creditTypes: string[];
    originators: string[];
  }>({ countries: [], creditTypes: [], originators: [] });
  const marketAllocationRequestIdRef = useRef(0);

  
  const { loading: pieChartLoading, execute: pieChartExecute } = useAsyncCallback(async (values: Values) => {
    const requestId = marketAllocationRequestIdRef.current + 1;
    marketAllocationRequestIdRef.current = requestId;

    const query = {
      productType: values.category ? [values.category] : undefined,
      providerId: values.originator ? [values.originator] : undefined,
      country: values.country ? [values.country] : undefined,
      currency: selectedCurrency,
    };

    const { data } = await dataSource.getMarketAllocationStatistics(query);

    if (marketAllocationRequestIdRef.current !== requestId) {
      return;
    }

    if (!values.country && !values.category && !values.originator) {
      setMarketAllocationFilterOptions({
        countries: Object.keys(data.byCountry).sort(),
        creditTypes: Object.keys(data.byCreditType).sort(),
        originators: Object.keys(data.byOriginator).sort(),
      });
    }

    setCharts([
      {
        labels: Object.keys(data.byCountry),
        datasets: [
          {
            id: 'country',
            ...colors,
            data: Object.values(data.byCountry).map((item) => item.sum),
            count: Object.values(data.byCountry).map((item) =>
              intl.formatMessage({ id: 'deals_count' }, { number: item.count }),
            ),
            currency: undefined,
            name: intl.formatMessage({ id: 'market_allocation_country' }),
          },
        ],
      },
      {
        labels: Object.keys(data.byCreditType).map((key) => intl.formatMessage({ id: `borrower.${key}` })),
        datasets: [
          {
            id: 'creditType',
            ...colors,
            data: Object.values(data.byCreditType).map((item) => item.sum),
            count: Object.values(data.byCreditType).map((item) =>
              intl.formatMessage({ id: 'deals_count' }, { number: item.count }),
            ),
            currency: undefined,
            name: intl.formatMessage({ id: 'market_allocation_credit_type' }),
          },
        ],
      },
      {
        labels: Object.keys(data.byOriginator),
        datasets: [
          {
            id: 'originator',
            ...colors,
            data: Object.values(data.byOriginator).map((item) => item.sum),
            count: Object.values(data.byOriginator).map((item) =>
              intl.formatMessage({ id: 'deals_count' }, { number: item.count }),
            ),
            currency: undefined,
            name: intl.formatMessage({ id: 'market_allocation_originator' }),
          },
        ],
      },
    ]);
  });

  const pieChartProps = useMemo((): Partial<ChartProps<'pie'>> => ({
    redraw: false,
    width: 177,
    height: 177,
    style: { margin: 'auto' },
    options: {
      responsive: false,
      animation: pieChartLoading ? false : undefined,
      plugins: {
        datalabels: {
          display: true,
          color: '#fff',
          align: 'center',
          textAlign: 'center',
          formatter: (value, context) => {
            const label = (context.chart.data?.labels ?? [])[context.dataIndex] ?? '';
            const datapoints = context.chart.data.datasets[0].data as number[];
            const totalValue = datapoints.reduce((acc, val) => acc + (val ?? 0), 0);
            if (!totalValue) return '';
            const percentage = ((Number(value) / totalValue) * 100).toFixed(1);
            return [label, `${percentage}%`];
          },
        },
        legend: { display: false },
        tooltip: {
          displayColors: false,
          callbacks: {
            title: (items) => {
              const item = items[0];
              const labelFromData = (item.chart.data.labels?.[item.dataIndex] as string | undefined) ?? '';
              const name = (item.dataset as any).name ?? '';
              return labelFromData ? labelFromData : name;
            },
            label: (item) => {
              const labelFromData = (item.chart.data.labels?.[item.dataIndex] as string | undefined) ?? '';
              const amount = formatMoney(moveDecimalPoint(item.raw as number, -8), { suffix: ' m' });
              const count = (item.dataset as any).count?.[item.dataIndex];
              return [amount, count ? `${count}` : null].filter(Boolean) as string[];
            },
          },
        },
      },
    },
    plugins: [ChartDataLabels],
  }), [intl, pieChartLoading]);
  
  const { result: marketResult, loading: marketLoading } = useAsync(async () => {
    if (!selectedCurrency) return null;

    const { data: marketOverviewData } = await dataSource.getMarketOverviewStatistics({
      currency: selectedCurrency,
    });

    return marketOverviewData;
  }, [selectedCurrency, dataSource]);

  const { result: listedOriginatorsResult, loading: listedLoading } = useAsync(async () => {
    const { data } = await dataSource.getMarketOverviewListedOriginators();
    return data;
  }, [dataSource]);

  const summary = marketResult?.summary;
  const tiles = marketResult?.tiles;
  const listedOriginators = useMemo(
    () => listedOriginatorsResult?.items ?? [],
    [listedOriginatorsResult],
  );

  const listedOriginatorRows = useMemo<ListedOriginatorGroupedRow[]>(() => {
    const rowsByOriginator = new Map<string, ListedOriginatorRow[]>();

    for (const item of listedOriginators) {
      const existingRows = rowsByOriginator.get(item.originator);

      if (existingRows) {
        existingRows.push(item);
      } else {
        rowsByOriginator.set(item.originator, [item]);
      }
    }

    return Array.from(rowsByOriginator.entries()).map(([originator, items]) => ({
      providerId: items[0].providerId,
      originator,
      items,
    }));
  }, [listedOriginators]);

  const listedOriginatorColumns = useMemo<Column<ListedOriginatorGroupedRow>[]>(
    () => [
      {
        id: 'originator',
        Header: (
          <Typography className={classes.listedOriginatorsHeaderText}>
            <FormattedMessage id="market_overview.listed_originators.column.originator" />
          </Typography>
        ),
        accessor: 'originator',
        Cell: ({ value }) => <Typography>{value}</Typography>,
      },
      {
        id: 'currency',
        Header: (
          <Typography className={classes.listedOriginatorsHeaderText}>
            <FormattedMessage id="market_overview.listed_originators.column.currency" />
          </Typography>
        ),
        accessor: 'items',
        Cell: ({ row }) => (
          <Stack spacing={0.25}>
            {row.original.items.map((item) => (
              <Typography key={`${item.originator}-${item.currency}`}>{item.currency}</Typography>
            ))}
          </Stack>
        ),
      },
      {
        id: 'primaryCreditType',
        Header: (
          <Typography className={classes.listedOriginatorsHeaderText}>
            <FormattedMessage id="market_overview.listed_originators.column.primary_credit_type" />
          </Typography>
        ),
        accessor: 'items',
        Cell: ({ row }) => (
          <Stack spacing={0.25}>
            {row.original.items.map((item) => (
              <Typography key={`${item.originator}-${item.currency}`}>{item.primaryCreditType}</Typography>
            ))}
          </Stack>
        ),
      },
      {
        id: 'averageExpectedXirr',
        Header: (
          <Typography align="right" className={classes.listedOriginatorsHeaderText}>
            <FormattedMessage id="market_overview.listed_originators.column.average_expected_xirr" />
          </Typography>
        ),
        accessor: 'items',
        className: 'text-right',
        Cell: ({ row }) => (
          <Stack spacing={0.25}>
            {row.original.items.map((item) => (
              <Typography key={`${item.originator}-${item.currency}`} align="right">
                {toPercent(item.averageExpectedXirr)}
              </Typography>
            ))}
          </Stack>
        ),
      },
      {
        id: 'currentPortfolio',
        Header: (
          <Typography align="right" className={classes.listedOriginatorsHeaderText}>
            <FormattedMessage id="market_overview.listed_originators.column.current_portfolio" />
          </Typography>
        ),
        accessor: 'items',
        className: 'text-right',
        Cell: ({ row }) => (
          <Stack spacing={0.25}>
            {row.original.items.map((item) => (
              <Typography key={`${item.originator}-${item.currency}`} align="right">
                {formatMoneyFromCentsWithCurrency(item.currentPortfolio, item.currency)}
              </Typography>
            ))}
          </Stack>
        ),
      },
      {
        id: 'performing',
        Header: (
          <Typography align="right" className={classes.listedOriginatorsHeaderText}>
            <FormattedMessage id="market_overview.listed_originators.column.performing" />
          </Typography>
        ),
        accessor: 'items',
        className: 'text-right',
        Cell: ({ row }) => (
          <Stack spacing={0.25}>
            {row.original.items.map((item) => (
              <Typography key={`${item.originator}-${item.currency}`} align="right">
                {toPercent(item.performing)}
              </Typography>
            ))}
          </Stack>
        ),
      },
      {
        id: 'par',
        Header: (
          <Typography align="right" className={classes.listedOriginatorsHeaderText}>
            <FormattedMessage id="market_overview.listed_originators.column.par" />
          </Typography>
        ),
        accessor: 'items',
        className: 'text-right',
        Cell: ({ row }) => (
          <Stack spacing={0.25}>
            {row.original.items.map((item) => (
              <Typography key={`${item.originator}-${item.currency}`} align="right">
                {toPercent(item.par)}
              </Typography>
            ))}
          </Stack>
        ),
      },
    ],
    [classes.listedOriginatorsHeaderText, formatMoneyFromCentsWithCurrency],
  );

  if (!selectedCurrency) return <PageContainer />;

  return (
    <PageContainer maxWidth={false}>
      <Grid container spacing={4} my={4}>
        <Grid item xs={12} lg={3}>
          <Stack>
            <Card>
              <Card.Body>
              <Typography fontWeight={700}><FormattedMessage id="market_overview.tile.deal_onboarding" /></Typography>
              <Typography mt={1.5} align="right" fontWeight={700}>{formatMoneyFromCents(tiles?.openPortfolio.newDealsAmount)}</Typography>
              </Card.Body>
            </Card>
            <Box className={classes.portfolioFlowConnection} aria-hidden="true" />
            <Card>
              <Card.Body>
              <Typography fontWeight={700}><FormattedMessage id="market_overview.open_portfolio.title" /></Typography>
              <Stack mt={1.5} spacing={1}>
                <Typography align="right" fontWeight={700}>
                  {formatMoneyFromCents(tiles?.openPortfolio.totalOpenDealsAmount)}
                </Typography>
                <Typography align="right">
                  <FormattedMessage id="market_overview.open_portfolio.open_loans" />{' '}
                  <Typography component="span" marginLeft={4} fontWeight={700}>{formatMoneyFromCents(tiles?.openPortfolio.openPrincipalAmount)}</Typography>
                </Typography>
              </Stack>
              </Card.Body>
            </Card>
            <Box className={classes.portfolioFlowConnection} aria-hidden="true" />
            <Card>
              <Card.Body>
              <Typography fontWeight={700}><FormattedMessage id="market_overview.tile.transition_to_current" /></Typography>
              <Typography mt={1.5} align="right" fontWeight={700}>{formatMoneyFromCents(tiles?.openPortfolio.startDealsAmount)}</Typography>
              </Card.Body>
            </Card>
            <Box className={classes.portfolioFlowConnection} aria-hidden="true" />
            <Card>
              <Card.Body>
              <Typography fontWeight={700}><FormattedMessage id="market_overview.current_portfolio.title" /></Typography>
              <Typography mt={1.5} align="right" fontWeight={700}>{formatMoneyFromCents(summary?.currentPortfolio.total)}</Typography>
              <Stack direction="row" justifyContent="space-between" gap={2} mt={1}>
                <Typography><FormattedMessage id="market_overview.current_portfolio.on_secondary_market" /></Typography>
                <Typography fontWeight={700} textAlign="right">
                  {formatMoneyFromCents(tiles?.currentPortfolio.onSecondaryMarketAmount)}
                </Typography>
              </Stack>
              </Card.Body>
            </Card>
          </Stack>
        </Grid>
        <Grid item xs={12} lg={9}>
          <Card>
            <Card.Header variant='condensed' title={<Typography
              fontWeight={700}><FormattedMessage id="market_overview.financed_portfolio" />
            </Typography>} />
            <Card.Body loadingContent={marketLoading}>
              <Grid container spacing={2} mb={3}>
                {[
                  { key: 'average_nominal_interest_rate', value: tiles?.currentPortfolio.averageNominalInterestRate, Icon: RiLineChartLine },
                  { key: 'average_expected_default_rate', value: tiles?.currentPortfolio.averageExpectedDefaultRate, Icon: RiAlertLine },
                  { key: 'average_expected_net_return', value: tiles?.currentPortfolio.averageExpectedNetReturn, Icon: RiArrowGoBackLine },
                ].map(({ key, value, Icon }) => (
                  <Grid item xs={12} sm={4} key={key} sx={{ display: 'flex' }}>
                    <Card sx={{ width: '100%' }}>
                      <Card.Body>
                        <Stack direction="row" alignItems="center" gap={1.5}>
                          <Icon size={28} aria-hidden="true" style={{ flexShrink: 0 }} />
                          <Typography><FormattedMessage id={`market_overview.current_portfolio.${key}`} /></Typography>
                        </Stack>
                        <Typography variant="h4" fontWeight={700} mt={2}>{toPercent(value ?? 0)}</Typography>
                      </Card.Body>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <FinancedPortfolioTable summary={summary} includeRecovery />
            </Card.Body>
          </Card>
        </Grid>

      </Grid>

      <Grid item my={4}>
        <Formik<Values>
          key={selectedCurrency}
          initialValues={{ country: undefined, category: undefined, originator: undefined }}
          onSubmit={pieChartExecute}
        >
          <Form>
            <Card>
              <Card.Header
                variant="condensed"
                title={intl.formatMessage({ id: 'market_allocation' })}
              />
              <Card.Body>
                <Stack spacing={2}>
                  <Box display="flex" gap="16px" flexDirection={{ xs: 'column', sm: 'row' }}>
                    <Stack spacing={3}>
                      <Field
                        id="country"
                        name="country"
                        sx={{ width: '180px' }}
                        component={Select}
                        options={[
                          { label: intl.formatMessage({ id: 'all' }), value: undefined },
                          ...marketAllocationFilterOptions.countries.map((value) => ({ value, label: value })),
                        ]}
                        label={intl.formatMessage({ id: 'filter_by_country' })}
                        variant="filter"
                        disableSelectedClass
                      />
                      <Field
                        id="category"
                        name="category"
                        sx={{ width: '180px' }}
                        component={Select}
                        options={[
                          { label: intl.formatMessage({ id: 'all' }), value: undefined },
                          ...marketAllocationFilterOptions.creditTypes.map((value) => ({
                            value,
                            label: intl.formatMessage({ id: `borrower.${value}` }),
                          })),
                        ]}
                        label={intl.formatMessage({ id: 'filter_by_product' })}
                        variant="filter"
                        disableSelectedClass
                      />
                      <Field
                        id="originator"
                        name="originator"
                        sx={{ width: '180px' }}
                        component={Select}
                        options={[
                          { label: intl.formatMessage({ id: 'all' }), value: undefined },
                          ...marketAllocationFilterOptions.originators.map((value) => ({ value, label: value })),
                        ]}
                        label={intl.formatMessage({ id: 'filter_by_originator' })}
                        variant="filter"
                        disableSelectedClass
                      />
                    </Stack>

                    <Box display="flex" flexGrow={1} justifyContent="space-around" gap="16px" flexWrap="wrap">
                      <div>
                        <Typography variant="body1" color="#222" fontWeight={600} mb={1} textAlign={'center'}>
                          {intl.formatMessage({ id: 'country' })}
                        </Typography>
                        {pieChartLoading ? (
                          <Box display="flex" justifyContent="center" alignItems="center" minHeight={177}>
                            <CircularProgress />
                          </Box>
                        ) : (
                          <Pie {...pieChartProps} datasetIdKey="id" data={charts[0]} />
                        )}
                      </div>
                      <div>
                        <Typography variant="body1" color="#222" fontWeight={600} mb={1} textAlign={'center'}>
                          {intl.formatMessage({ id: 'borrower.type' })}
                        </Typography>
                        {pieChartLoading ? (
                          <Box display="flex" justifyContent="center" alignItems="center" minHeight={177}>
                            <CircularProgress />
                          </Box>
                        ) : (
                          <Pie {...pieChartProps} datasetIdKey="id" data={charts[1]} />
                        )}
                      </div>
                      <div>
                        <Typography variant="body1" color="#222" fontWeight={600} mb={1} textAlign={'center'}>
                          {intl.formatMessage({ id: 'originator' })}
                        </Typography>
                        {pieChartLoading ? (
                          <Box display="flex" justifyContent="center" alignItems="center" minHeight={177}>
                            <CircularProgress />
                          </Box>
                        ) : (
                          <Pie {...pieChartProps} datasetIdKey="id" data={charts[2]} />
                        )}
                      </div>
                    </Box>
                  </Box>

                  <SubmitFormOnChange initialSubmit />
                </Stack>
              </Card.Body>
            </Card>
          </Form>
        </Formik>
      </Grid>
      
      <Grid item my={4}>
        <Card>
          <Card.Header
            variant="condensed"
            title={(
              <Stack spacing={0.5}>
                <Typography fontWeight={700}>
                  <FormattedMessage id="market_overview.listed_originators.title" />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <FormattedMessage id="open_row_details" />
                </Typography>
              </Stack>
            )}
          />
          <Card.Body loadingContent={listedLoading}>
            <Table<ListedOriginatorGroupedRow>
              columns={listedOriginatorColumns}
              data={listedOriginatorRows}
              withPagination={false}
              sortable={false}
              className={classes.listedOriginatorsTable}
              rowOnClick={(_, row) => {
                history.push(`${url}/${row.original.providerId}`);
                window.scroll(0,0)
              }}
            />
          </Card.Body>
        </Card>
      </Grid>
    </PageContainer>
  );
};
